'use es6';

import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { StyledUIButtonGroup, StyledHandleButton as StyledHandleButtonComponent, StyledIconButton as StyledIconButtonComponent } from './styledButtons';
import ToolbarGrippies from './ToolbarGrippies';
import ToolbarDropdownIconButton from './ToolbarDropdownIconButton';
import ToolbarActionsPopover from './ToolbarActionsPopover';
import { DEFAULT_RESPONSIVE_SIZE, MEDIUM_RESPONSIVE_SIZE, SMALL_RESPONSIVE_SIZE, CORNER_PLACEMENTS } from './constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class BaseToolbar extends PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      tooltipOpen: false
    };
    this.handleDropdownClick = () => {
      const {
        onDropdownOpenChange
      } = this.props;
      this.setState(prevState => {
        const tooltipOpen = !prevState.tooltipOpen;
        setTimeout(() => onDropdownOpenChange(tooltipOpen), 0);
        return {
          tooltipOpen
        };
      });
    };
    this.handleDropdownOpenChange = evt => {
      const {
        onDropdownOpenChange
      } = this.props;
      const tooltipOpen = evt.target.value;
      this.setState({
        tooltipOpen
      });
      onDropdownOpenChange(tooltipOpen);
    };
    this.executeAction = ({
      callback
    }) => {
      const {
        onDropdownOpenChange
      } = this.props;
      const {
        tooltipOpen
      } = this.state;
      if (tooltipOpen) {
        this.setState({
          tooltipOpen: false
        });
        onDropdownOpenChange(false);
      }
      callback();
    };
    this.renderDropdownButton = buttonProps => {
      const {
        active,
        actions,
        fontSize,
        height,
        lastButtonRounding,
        onlyLeftAndRightButtonBorders
      } = this.props;
      const {
        tooltipOpen
      } = this.state;
      const buttonActions = actions.filter(a => !a.onlyInDropdown);
      const hasInlineActions = !!buttonActions && buttonActions.length > 0;
      const buttonColor = active ? 'tertiary' : 'tertiary-light';
      const hasAnyBorder = !onlyLeftAndRightButtonBorders || hasInlineActions;
      return /*#__PURE__*/_jsx(ToolbarDropdownIconButton, Object.assign({}, buttonProps, {
        use: buttonColor,
        active: tooltipOpen,
        fontSize: fontSize,
        height: height,
        lineHeight: height,
        hasAnyBorder: hasAnyBorder,
        onlyLeftAndRightBorders: onlyLeftAndRightButtonBorders,
        cornerToRound: lastButtonRounding
      }));
    };
  }
  renderActionButtons({
    buttonActions,
    isDefault,
    buttonColor,
    hasDropdown
  }) {
    if (!isDefault) {
      return null;
    }
    const {
      active,
      lastButtonRounding,
      iconSize,
      lineHeight,
      height,
      onlyLeftAndRightButtonBorders,
      StyledIconButton
    } = this.props;
    return buttonActions.map(({
      id: actionId,
      callback,
      icon: actionIcon,
      iconInstanceSize,
      iconClassName,
      tooltipLabel,
      disabled,
      placement,
      autoPlacement
    }, i) => {
      const isFirstButtonAfterHandle = i === 0;
      const isLastButton = i === buttonActions.length - 1;
      const cornerToRound = !hasDropdown && isLastButton ? lastButtonRounding : undefined;
      const hasAnyBorder = !isFirstButtonAfterHandle || !onlyLeftAndRightButtonBorders;
      const allIconClasses = `${iconClassName} ${disabled ? 'disabled-icon' : ''}`;
      const sizeForIcon = iconInstanceSize || iconSize;
      return /*#__PURE__*/_jsx(StyledIconButton, {
        use: buttonColor,
        active: active,
        disabled: disabled,
        size: "xs",
        onClick: disabled ? undefined : callback,
        fontSize: sizeForIcon,
        placement: placement,
        autoPlacement: autoPlacement,
        height: height,
        lineHeight: lineHeight,
        hasAnyBorder: hasAnyBorder,
        onlyLeftAndRightBorders: onlyLeftAndRightButtonBorders,
        cornerToRound: cornerToRound,
        tooltip: tooltipLabel,
        children: /*#__PURE__*/_jsx(UIIcon, {
          name: actionIcon,
          size: sizeForIcon,
          className: allIconClasses
        })
      }, actionId);
    });
  }
  renderActionsDropdown({
    dropdownActions
  }) {
    const {
      dropdownPlacement,
      ActionsDropdown
    } = this.props;
    const {
      tooltipOpen
    } = this.state;
    if (dropdownActions.length === 0) {
      return null;
    }
    return /*#__PURE__*/_jsx(ActionsDropdown, {
      open: tooltipOpen,
      onOpenChange: this.handleDropdownOpenChange,
      executeAction: this.executeAction,
      placement: dropdownPlacement,
      actions: dropdownActions,
      Button: this.renderDropdownButton
    });
  }
  renderHandleIcon({
    isSmall
  }) {
    const {
      icon,
      showHandleGrippie
    } = this.props;

    // Hide icon when in small size (unless there are no handle grippies)
    if (isSmall && showHandleGrippie) {
      // We at least need _some_ text in this element even when the icon is removed
      return '';
    }
    return icon;
  }
  renderFirstHandleButtonHelper(options) {
    const {
      isMedium,
      isDefault,
      isSmall,
      buttonColor
    } = options;
    const {
      active,
      firstButtonRounding,
      fontSize,
      height,
      showHandleGrippie,
      label,
      lineHeight,
      onHandleMouseDown,
      onHandleClick,
      StyledHandleButton,
      draggable
    } = this.props;
    return /*#__PURE__*/_jsxs(StyledHandleButton, {
      use: buttonColor,
      size: "xs",
      active: active,
      height: height,
      fontSize: fontSize,
      lineHeight: lineHeight,
      hasIcon: isDefault || isMedium || isSmall && !showHandleGrippie,
      hasAnyBorder: true,
      showHandleGrippie: showHandleGrippie,
      cornerToRound: firstButtonRounding,
      onMouseDown: onHandleMouseDown,
      onClick: onHandleClick,
      draggable: draggable,
      children: [showHandleGrippie && /*#__PURE__*/_jsx(ToolbarGrippies, {
        toolbarHeight: height,
        useDark: active
      }), this.renderHandleIcon(options), isDefault && label ? /*#__PURE__*/_jsx("span", {
        className: "toolbar-handle-label",
        children: label
      }) : null]
    });
  }
  renderFirstHandleButton(options) {
    const {
      renderWrappedHandle
    } = this.props;
    const handleButton = this.renderFirstHandleButtonHelper(options);
    if (renderWrappedHandle) {
      return renderWrappedHandle({
        handleButton,
        props: this.props
      });
    }
    return handleButton;
  }
  renderFirstHandleButtonWithTooltipIfNeeded(options) {
    const {
      isDefault
    } = options;
    const {
      label,
      handleTooltip,
      handleTooltipPlacement
    } = this.props;
    const tooltipToUse = handleTooltip || label;
    const showingALabel = label && isDefault;
    const innerElements = this.renderFirstHandleButton(options);
    if (!tooltipToUse || showingALabel) {
      return innerElements;
    }
    return /*#__PURE__*/_jsx(UITooltip, {
      placement: handleTooltipPlacement,
      title: tooltipToUse,
      children: innerElements
    });
  }
  render() {
    const {
      active,
      className,
      style,
      actions,
      responsiveSize,
      forwardedRef
    } = this.props;
    const isSmall = responsiveSize === SMALL_RESPONSIVE_SIZE;
    const isMedium = responsiveSize === MEDIUM_RESPONSIVE_SIZE;
    const isDefault = responsiveSize === DEFAULT_RESPONSIVE_SIZE;
    const buttonColor = active ? 'tertiary' : 'tertiary-light';
    let buttonActions;
    let dropdownActions;

    // Hide all of action buttons in the medium and small size
    if (isDefault) {
      buttonActions = actions.filter(a => !a.onlyInDropdown);
      dropdownActions = actions.filter(a => a.onlyInDropdown);
    } else {
      dropdownActions = actions;
    }
    return /*#__PURE__*/_jsxs(StyledUIButtonGroup, {
      ref: forwardedRef,
      className: className,
      style: style,
      children: [this.renderFirstHandleButtonWithTooltipIfNeeded({
        isSmall,
        isMedium,
        isDefault,
        buttonColor
      }), this.renderActionButtons({
        buttonActions,
        isDefault,
        buttonColor,
        hasDropdown: dropdownActions.length > 0
      }), this.renderActionsDropdown({
        dropdownActions
      })]
    });
  }
}
BaseToolbar.propTypes = {
  active: PropTypes.bool.isRequired,
  className: PropTypes.string,
  responsiveSize: PropTypes.string,
  dropdownPlacement: PropTypes.string,
  actions: PropTypes.array.isRequired,
  fontSize: PropTypes.number.isRequired,
  iconSize: PropTypes.number.isRequired,
  lineHeight: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  firstButtonRounding: PropTypes.oneOf(CORNER_PLACEMENTS),
  lastButtonRounding: PropTypes.oneOf(CORNER_PLACEMENTS),
  onlyLeftAndRightButtonBorders: PropTypes.bool.isRequired,
  style: PropTypes.object,
  label: PropTypes.node,
  handleTooltip: PropTypes.node,
  handleTooltipPlacement: PropTypes.string,
  icon: PropTypes.node.isRequired,
  renderWrappedHandle: PropTypes.func,
  onHandleMouseDown: PropTypes.func,
  onDropdownOpenChange: PropTypes.func,
  onHandleClick: PropTypes.func,
  showHandleGrippie: PropTypes.bool.isRequired,
  StyledIconButton: PropTypes.elementType,
  StyledHandleButton: PropTypes.elementType,
  ActionsDropdown: PropTypes.elementType,
  draggable: PropTypes.bool,
  forwardedRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  })])
};
BaseToolbar.defaultProps = {
  responsiveSize: DEFAULT_RESPONSIVE_SIZE,
  showHandleGrippie: true,
  dropdownPlacement: 'bottom',
  onlyLeftAndRightButtonBorders: false,
  style: {},
  onDropdownOpenChange: () => {},
  StyledIconButton: StyledIconButtonComponent,
  StyledHandleButton: StyledHandleButtonComponent,
  ActionsDropdown: ToolbarActionsPopover,
  draggable: false
};
export default BaseToolbar;